import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// ─── Admin password (change this to your own secret) ───────────────
// NOTE: This is a client-side gate — good enough to keep casual visitors
// out, but the password lives in your JS bundle. For bank-level security
// you'd need a backend. For a lead dashboard this is perfectly fine.
const ADMIN_PASSWORD = "KIWIORA@ONE";

// ─── Brand tokens (new KiwiOra palette – matches App.js) ───────────
// Deep Emerald Green #0D4B2D · Antique Gold #B8860B · Warm Cream #FAF8F3
// Pure White #FFFFFF · Crimson Red #C0392B · Dark Forest Charcoal #14241B
const C = {
  navy: "#0D4B2D",        // Deep Emerald Green – headings, banners, nav
  navyDeep: "#0D4B2D",
  skyBlue: "#B8860B",     // Antique Gold – highlights, active states
  accent: "#B8860B",      // Antique Gold – eyebrows, focus rings
  gold: "#B8860B",
  goldBorder: "rgba(184, 134, 11, 0.35)",
  goldSoft: "rgba(184, 134, 11, 0.10)",
  goldShadow: "0 8px 24px rgba(184, 134, 11, 0.12)",
  dark: "#0D4B2D",        // landing banner background
  formWrap: "#0D4B2D",    // green frame around the form
  btnPrimary: "#C0392B",  // Crimson Red – primary CTAs
  redDark: "#A93226",     // CTA hover
  inputBg: "#FAF8F3",     // Warm Cream fields
  inputBorder: "rgba(184, 134, 11, 0.35)",
  pageBg: "#FAF8F3",      // Warm Cream page background
  cardBg: "#FFFFFF",      // Pure White cards
  lightGray: "#FAF8F3",
  gray200: "#E6DFCF",     // warm neutral for borders / inactive states
  gray400: "#8A958E",
  gray600: "#4A5A50",
  textDark: "#14241B",    // Dark Forest Charcoal
  textLabel: "#14241B",
  cream: "#FAF8F3",
  white: "#FFFFFF",
  red: "#C0392B",
  orange: "#B8860B",
  green: "#0D4B2D",
  teal: "#0D4B2D",
};

const GOOGLE_SHEETS_URL =
  "https://script.google.com/macros/s/AKfycbzJ9K50BzKrbqoSIJEjBmvHc8D4Y0SblAbV0CESwl899Uz9YyZPPpUrJi8_bLTApVdWXA/exec";

// ─── Animations ────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled components (matching your existing patterns) ───────────
const PageWrapper = styled.div`
  font-family: "Fira Sans", -apple-system, BlinkMacSystemFont, "Segoe UI",
    system-ui, sans-serif;
  background: ${C.pageBg};
  color: ${C.textDark};
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 700px) {
    padding: 0 16px;
  }
`;

// ─── Hero ──────────────────────────────────────────────────────────
const HeroBg = styled.section`
  background: ${C.navy};
  color: ${C.white};
  padding: 72px 24px 80px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -60px;
    right: -80px;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    border: 2px solid rgba(184, 134, 11, 0.25);
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -40px;
    left: -40px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    border: 2px solid rgba(184, 134, 11, 0.15);
    pointer-events: none;
  }

  @media (max-width: 700px) {
    padding: 48px 16px 60px;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: ${C.accent};
  margin-bottom: 14px;
`;

const HeroH1 = styled.h1`
  font-family: "Fira Sans", sans-serif;
  font-size: 2.8rem;
  font-weight: 900;
  line-height: 1.12;
  margin: 0 auto 16px;
  max-width: 640px;
  letter-spacing: 0.5px;
  @media (max-width: 700px) {
    font-size: 1.8rem;
  }
`;

const HeroSub = styled.p`
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.75);
  max-width: 500px;
  margin: 0 auto 32px;
  line-height: 1.6;
  @media (max-width: 700px) {
    font-size: 1rem;
  }
`;

const CtaBtn = styled.button`
  background: ${C.red};
  color: ${C.white};
  font-family: inherit;
  font-size: 1.1rem;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  padding: 15px 38px;
  cursor: pointer;
  box-shadow: 0 4px 18px rgba(192, 57, 43, 0.28);
  transition: background 0.18s, transform 0.15s;
  &:hover {
    background: ${C.redDark};
    transform: translateY(-1px);
  }
`;

const CtaBtnDark = styled(CtaBtn)`
  background: ${C.btnPrimary};
  box-shadow: 0 2px 12px rgba(192, 57, 43, 0.2);
  &:hover {
    background: ${C.redDark};
  }
`;

const CtaBtnOutline = styled.button`
  background: none;
  color: ${C.gray600};
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid ${C.gray200};
  border-radius: 10px;
  padding: 10px 22px;
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover {
    border-color: ${C.skyBlue};
    color: ${C.skyBlue};
  }
`;

// ─── Value Props ───────────────────────────────────────────────────
const PropsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 0;
  max-width: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 2;
`;

const PropCard = styled.div`
  background: ${C.white};
  padding: 32px 28px;
  border-right: 1px solid ${C.goldBorder};
  border-bottom: 1px solid ${C.goldBorder};
  animation: ${fadeUp} 0.45s ease both;
  animation-delay: ${(p) => p.delay || "0s"};

  &:last-child {
    border-right: none;
  }

  @media (max-width: 700px) {
    border-right: none;
    padding: 24px 22px;
  }
`;

const PropEmoji = styled.div`
  font-size: 1.6rem;
  margin-bottom: 10px;
`;
const PropTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${C.navy};
  margin-bottom: 5px;
`;
const PropDesc = styled.div`
  font-size: 0.88rem;
  color: ${C.gray600};
  line-height: 1.55;
`;

// ─── Form area ─────────────────────────────────────────────────────
const FormSection = styled.section`
  max-width: 520px;
  margin: 40px auto 0;
  padding: 0 16px 48px;
`;

const FormWrap = styled.div`
  background: ${C.formWrap};
  border: 1px solid ${C.gold};
  border-radius: 18px;
  padding: 24px 18px;
  box-shadow: 0 16px 40px rgba(13, 75, 45, 0.18);
`;

const FormTitle = styled.h2`
  text-align: center;
  font-size: 1.2rem;
  font-weight: 500;
  color: ${C.cream};
  margin: 0 0 2px;
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: rgba(250, 248, 243, 0.8);
  font-size: 0.82rem;
  margin: 0 0 14px;
`;

const FormCard = styled.div`
  background: ${C.cardBg};
  border-radius: 22px;
  padding: 28px 24px 22px;
  box-shadow: 0 8px 36px rgba(0, 0, 0, 0.12);
`;

const Label = styled.label`
  font-weight: 600;
  color: ${C.textLabel};
  font-size: 0.88rem;
  display: block;
  margin-bottom: 4px;
`;

const Input = styled.input`
  background: ${C.inputBg};
  color: ${C.textDark};
  border-radius: 9px;
  border: 1.5px solid ${(p) => (p.$error ? C.red : C.inputBorder)};
  padding: 9px 12px;
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 4px;
  transition: border 0.14s;
  &:focus {
    border-color: ${C.accent};
  }
`;

const Select = styled.select`
  background: ${C.inputBg};
  color: ${C.textDark};
  border-radius: 9px;
  border: 1.5px solid ${(p) => (p.$error ? C.red : C.inputBorder)};
  padding: 9px 12px;
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 4px;
  transition: border 0.14s;
  &:focus {
    border-color: ${C.accent};
  }
`;

const Err = styled.div`
  color: ${C.red};
  font-size: 0.75rem;
  margin-bottom: 2px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  & > * {
    flex: 1;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.cols || 2}, 1fr);
  gap: 8px;
`;

const OptionBtn = styled.button`
  padding: 9px 10px;
  border-radius: 9px;
  border: 1.5px solid ${(p) => (p.$active ? C.skyBlue : C.inputBorder)};
  background: ${(p) => (p.$active ? C.goldSoft : C.cardBg)};
  color: ${(p) => (p.$active ? C.navy : C.gray600)};
  font-size: 0.82rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: all 0.13s;
  &:hover {
    border-color: ${C.skyBlue};
  }
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 22px;
`;

const ProgressDot = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${(p) => (p.$done ? C.skyBlue : p.$active ? C.navy : C.gray200)};
  color: ${(p) => (p.$done || p.$active ? C.white : C.gray400)};
  transition: all 0.2s;
  flex-shrink: 0;
`;

const ProgressLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${(p) => (p.$done ? C.skyBlue : C.gray200)};
  transition: background 0.3s;
`;

const ProgressLabel = styled.div`
  font-size: 0.68rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? C.navy : C.gray400)};
  margin-top: 4px;
  text-align: center;
`;

const SendBtn = styled.button`
  margin-top: 14px;
  width: 100%;
  background: ${C.btnPrimary};
  color: ${C.white};
  font-size: 1.05rem;
  font-weight: 700;
  font-family: inherit;
  border: none;
  border-radius: 12px;
  padding: 13px 0;
  cursor: pointer;
  box-shadow: 0 2px 12px rgba(192, 57, 43, 0.2);
  transition: background 0.15s, transform 0.17s;
  &:hover {
    background: ${C.redDark};
    transform: scale(1.02);
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
    transform: none;
  }
`;

const NavBtn = styled.button`
  background: none;
  border: 1px solid ${C.gray200};
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${C.gray600};
  cursor: pointer;
  font-weight: 500;
  &:hover {
    border-color: ${C.skyBlue};
  }
`;

const NextBtn = styled.button`
  background: ${C.navy};
  color: ${C.white};
  border: none;
  border-radius: 10px;
  padding: 10px 26px;
  font-size: 0.9rem;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  &:hover {
    background: ${C.skyBlue};
  }
`;

// ─── Thank You ─────────────────────────────────────────────────────
const ThankYouCard = styled.div`
  background: ${C.cardBg};
  border-radius: 20px;
  padding: 44px 28px;
  max-width: 460px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  border: 1px solid ${C.goldBorder};
  border-top: 3px solid ${C.gold};
  box-shadow: ${C.goldShadow};
`;

// ─── Admin Dashboard ───────────────────────────────────────────────
const AdminNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: ${C.navy};
  color: ${C.white};
  border-bottom: 2px solid ${C.gold};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
  margin-bottom: 22px;
`;

const StatBox = styled.div`
  background: ${(p) => p.bg || C.lightGray};
  border-radius: 14px;
  padding: 16px 14px;
  border: 1px solid ${C.goldBorder};
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${(p) => p.color || C.navy};
`;

const StatLabel2 = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${C.gray400};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  align-items: center;
`;

const FilterBtn = styled.button`
  padding: 7px 16px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? C.navy : C.gray200)};
  background: ${(p) => (p.$active ? C.navy : C.cardBg)};
  color: ${(p) => (p.$active ? C.white : C.gray600)};
  font-size: 0.82rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${C.gray200};
  font-size: 0.82rem;
  font-family: inherit;
  outline: none;
  &:focus {
    border-color: ${C.accent};
  }
`;

const LeadCard = styled.div`
  background: ${C.cardBg};
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid ${C.goldBorder};
  box-shadow: ${C.goldShadow};
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const TierBadge = styled.span`
  background: ${(p) =>
    p.tier === "Hot" ? "rgba(192, 57, 43, 0.10)" : p.tier === "Warm" ? C.goldSoft : C.lightGray};
  color: ${(p) =>
    p.tier === "Hot" ? C.red : p.tier === "Warm" ? C.orange : C.gray400};
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 700;
`;

const ExportBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 8px;
  border: 1px solid ${C.gray200};
  background: ${C.cardBg};
  font-size: 0.82rem;
  font-family: inherit;
  color: ${C.gray600};
  cursor: pointer;
  font-weight: 500;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 8px;
  border: 1px solid ${C.gray200};
  background: ${C.cardBg};
  font-size: 0.82rem;
  font-family: inherit;
  color: ${C.gray600};
  cursor: pointer;
  font-weight: 500;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const EmptyState = styled.div`
  background: ${C.cardBg};
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  border: 1px solid ${C.goldBorder};
`;

// ─── Lead Scoring ──────────────────────────────────────────────────
function scoreLead(d) {
  let score = 0;
  const reasons = [];

  // Timeline
  if (d.timeline === "Within 3 months") {
    score += 30;
    reasons.push("Immediate timeline");
  } else if (d.timeline === "3–6 months") {
    score += 22;
    reasons.push("Near-term timeline");
  } else if (d.timeline === "6–12 months") {
    score += 12;
  } else {
    score += 5;
  }

  // Budget
  if (d.budget === "₹20–40 Lakhs" || d.budget === "₹40 Lakhs+") {
    score += 25;
    reasons.push("Strong budget");
  } else if (d.budget === "₹10–20 Lakhs") {
    score += 15;
    reasons.push("Moderate budget");
  } else {
    score += 5;
  }

  // Test readiness
  if (d.testScore === "Yes, score ready") {
    score += 20;
    reasons.push("Test score ready");
  } else if (d.testScore === "Booked, not taken") {
    score += 10;
  } else {
    score += 3;
  }

  // Visa type
  if (d.visaType === "Student Visa") {
    score += 15;
    reasons.push("Student visa interest");
  } else if (d.visaType === "Post-Study Work Visa") {
    score += 12;
  } else {
    score += 8;
  }

  // Funding
  if (d.funding === "Self-funded") {
    score += 10;
    reasons.push("Self-funded");
  } else if (d.funding === "Education loan") {
    score += 7;
  } else {
    score += 3;
  }

  let tier = "Cold";
  if (score >= 70) tier = "Hot";
  else if (score >= 45) tier = "Warm";

  return { score, tier, reasons };
}

// ─── Options ───────────────────────────────────────────────────────
const VISA_TYPES = [
  "Student Visa",
  "Partner of Student Work Visa",
  "Dependent Visa",
  "Post-Study Work Visa",
];
const TIMELINES = [
  "Within 3 months",
  "3–6 months",
  "6–12 months",
  "12+ months",
];
const BUDGETS = [
  "Below ₹10 Lakhs",
  "₹10–20 Lakhs",
  "₹20–40 Lakhs",
  "₹40 Lakhs+",
  "Need guidance",
];
const TEST_OPTIONS = [
  "Yes, score ready",
  "Booked, not taken",
  "Not yet planned",
];
const FUNDING = [
  "Self-funded",
  "Education loan",
  "Scholarship",
  "Not sure yet",
];

// ─── Local storage (fallback for the submitting device) ────────────
const LS_KEY = "kiwiora_leads";
function loadLeads() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveLeads(leads) {
  localStorage.setItem(LS_KEY, JSON.stringify(leads));
}

// ─── Fetch ALL leads from the Google Sheet (cross-device) ──────────
// Requires a doGet() in your Apps Script that returns the sheet rows
// as JSON. See the setup notes. This maps the sheet's columns onto the
// shape the dashboard expects, tolerating a few common header variants.
async function fetchSheetLeads() {
  const res = await fetch(GOOGLE_SHEETS_URL);
  const rows = await res.json();

  if (!Array.isArray(rows)) return [];

  return rows
    .map((r, i) => {
      const score = Number(r.leadScore ?? r.score ?? r.Score ?? 0);
      const tier =
        r.leadTier ||
        r.tier ||
        r.Tier ||
        (score >= 70 ? "Hot" : score >= 45 ? "Warm" : "Cold");

      return {
        id: r.id || `sheet-${i}`,
        firstName: r.firstName || r["First Name"] || "",
        lastName: r.lastName || r["Last Name"] || "",
        email: r.email || r.Email || "",
        phone: r.phone || r.Phone || "",
        city: r.city || r.City || "",
        education: r.education || r.Education || "",
        visaType: r.visaType || r["Visa Type"] || "",
        timeline: r.timeline || r.Timeline || "",
        budget: r.budget || r.Budget || "",
        testScore: r.testScore || r["Test Score"] || "",
        funding: r.funding || r.Funding || "",
        message: r.message || r.Message || "",
        score,
        tier,
        reasons: [],
        status: r.status || r.Status || "New",
        createdAt:
          r.Timestamp ||
          r.timestamp ||
          r.createdAt ||
          r.Date ||
          new Date().toISOString(),
      };
    })
    .reverse(); // newest first
}

// ═══════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function LeadGenerator() {
  const location = useLocation();
  const [view, setView] = useState("landing"); // landing | form | thankyou | admin | login
  const [step, setStep] = useState(0);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastLead, setLastLead] = useState(null);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [fetchError, setFetchError] = useState("");

  // ─── Admin auth ──────────────────────────────────────────────────
  const [adminAuthed, setAdminAuthed] = useState(
    () => sessionStorage.getItem("kiwiora_admin") === "true"
  );
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");

  const handleAdminLogin = () => {
    if (pwInput === ADMIN_PASSWORD) {
      setAdminAuthed(true);
      sessionStorage.setItem("kiwiora_admin", "true");
      setPwInput("");
      setPwError("");
      setView("admin");
    } else {
      setPwError("Incorrect password");
    }
  };

  const handleAdminLogout = () => {
    setAdminAuthed(false);
    sessionStorage.removeItem("kiwiora_admin");
    setView("landing");
  };

  // ─── Load all leads from the Sheet (cross-device) ────────────────
  const refreshLeads = async () => {
    setLoadingLeads(true);
    setFetchError("");
    try {
      const sheetLeads = await fetchSheetLeads();
      setLeads(sheetLeads);
    } catch (err) {
      console.error("Sheet fetch failed:", err);
      setFetchError(
        "Couldn't load leads from the sheet. Showing this device's local copy instead."
      );
      setLeads(loadLeads()); // fall back to whatever this browser has
    } finally {
      setLoadingLeads(false);
    }
  };

  // Navigate to admin login if ?admin is in the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("admin")) {
      if (adminAuthed) {
        setView("admin");
      } else {
        setView("login");
      }
    }
  }, [location.search]);

  // When the dashboard opens, pull the full list from the Sheet
  useEffect(() => {
    if (view === "admin" && adminAuthed) {
      refreshLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, adminAuthed]);
  // ────────────────────────────────────────────────────────────────

  const isMobile = useMediaQuery({ maxWidth: 700 });

  const blank = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    education: "",
    visaType: "",
    timeline: "",
    budget: "",
    testScore: "",
    funding: "",
    message: "",
  };
  const [form, setForm] = useState(blank);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  // ─── Validation ──────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.firstName.trim()) e.firstName = "Required";
      if (!form.lastName.trim()) e.lastName = "Required";
      if (
        !form.email.trim() ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
      )
        e.email = "Valid email required";
      if (!phone || phone.replace(/\D/g, "").length < 10)
        e.phone = "Valid phone required";
    }
    if (step === 1) {
      if (!form.visaType) e.visaType = "Pick a visa type";
      if (!form.timeline) e.timeline = "Required";
    }
    if (step === 2) {
      if (!form.budget) e.budget = "Required";
      if (!form.testScore) e.testScore = "Required";
      if (!form.funding) e.funding = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) setStep((s) => s + 1);
  };

  // ─── Submit to Google Sheets + local store ───────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const result = scoreLead(form);

    const payload = {
      ...form,
      phone,
      countryCode,
      leadScore: result.score,
      leadTier: result.tier,
    };

    // 1. Push to Google Sheets (same endpoint as your Appointment form)
    try {
      await fetch(GOOGLE_SHEETS_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Sheet push failed:", err);
    }

    // 2. Save locally as a fallback for this device
    const lead = {
      ...form,
      phone,
      countryCode,
      id:
        Date.now().toString(36) +
        Math.random().toString(36).slice(2, 6),
      score: result.score,
      tier: result.tier,
      reasons: result.reasons,
      status: "New",
      createdAt: new Date().toISOString(),
    };
    const existing = loadLeads();
    saveLeads([lead, ...existing]);
    setLastLead(lead);

    setSubmitting(false);
    setView("thankyou");
    setStep(0);
    setForm(blank);
    setPhone("");
  };

  // ─── Admin helpers ───────────────────────────────────────────────
  // NOTE: status changes and deletes are LOCAL to the dashboard view
  // only. Because leads now come from the Sheet, these do not write
  // back to it — a refresh will restore the sheet's values. Ask if you
  // want status/delete to persist to the Sheet.
  const updateStatus = (id, status) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, status } : l))
    );
  };

  const deleteLead = (id) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter !== "All" && l.tier !== filter) return false;
      if (
        search &&
        !`${l.firstName} ${l.lastName} ${l.email} ${l.city}`
          .toLowerCase()
          .includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [leads, filter, search]);

  const stats = useMemo(
    () => ({
      total: leads.length,
      hot: leads.filter((l) => l.tier === "Hot").length,
      warm: leads.filter((l) => l.tier === "Warm").length,
      cold: leads.filter((l) => l.tier === "Cold").length,
    }),
    [leads]
  );

  const exportCSV = () => {
    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Phone",
      "City",
      "Education",
      "Visa Type",
      "Timeline",
      "Budget",
      "Test Score",
      "Funding",
      "Message",
      "Score",
      "Tier",
      "Status",
      "Date",
    ];
    const rows = filtered.map((l) =>
      [
        l.firstName,
        l.lastName,
        l.email,
        l.phone,
        l.city,
        l.education,
        l.visaType,
        l.timeline,
        l.budget,
        l.testScore,
        l.funding,
        l.message,
        l.score,
        l.tier,
        l.status,
        (() => {
          const d = new Date(l.createdAt);
          return isNaN(d) ? l.createdAt : d.toLocaleDateString();
        })(),
      ]
        .map((v) => `"${(v || "").toString().replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kiwiora-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (v) => {
    const d = new Date(v);
    return isNaN(d) ? v || "" : d.toLocaleDateString();
  };

  // ═════════════════════════════════════════════════════════════════
  //  LANDING PAGE
  // ═════════════════════════════════════════════════════════════════
  if (view === "landing")
    return (
      <PageWrapper style={{ background: C.dark }}>
        <div
          style={{
            background: C.dark,
            color: C.white,
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: isMobile ? "48px 20px" : "72px 32px",
            borderBottom: `3px solid ${C.gold}`,
          }}
        >
          <h1
            style={{
              fontFamily: "'Fira Sans', sans-serif",
              fontSize: isMobile ? "1.85rem" : "3rem",
              fontWeight: 900,
              lineHeight: 1.15,
              letterSpacing: "1px",
              margin: "0 auto 18px",
              maxWidth: 620,
              color: C.white,
            }}
          >
            Start Your New Zealand Journey Today
          </h1>

          <p
            style={{
              fontFamily: "'Fira Sans', sans-serif",
              fontSize: isMobile ? "1rem" : "1.3rem",
              fontWeight: 400,
              color: C.cream,
              maxWidth: 520,
              margin: "0 auto 14px",
              lineHeight: 1.6,
            }}
          >
            Get a free personalised consultation. We match you with the
            right visa pathway and guide you through every step, from
            application to arrival.
          </p>

          <p
            style={{
              fontFamily: "'Fira Sans', sans-serif",
              fontSize: isMobile ? "0.88rem" : "0.95rem",
              fontWeight: 300,
              color: "rgba(250,248,243,0.7)",
              maxWidth: 440,
              margin: "0 auto 36px",
              lineHeight: 1.55,
            }}
          >
            Takes less than 2 minutes. No commitments.
          </p>

          <button
            onClick={() => {
              setView("form");
              setStep(0);
            }}
            style={{
              fontFamily: "'Fira Sans', sans-serif",
              background: C.red,
              color: C.white,
              fontWeight: 700,
              boxShadow: "0 4px 14px rgba(192, 57, 43, 0.3)",
              fontSize: isMobile ? "0.95rem" : "1.09rem",
              border: "none",
              borderRadius: 10,
              padding: isMobile ? "12px 28px" : "12px 36px",
              cursor: "pointer",
              display: "inline-block",
            }}
          >
            Click Here To Start
          </button>
        </div>
      </PageWrapper>
    );

  // ═════════════════════════════════════════════════════════════════
  //  MULTI-STEP FORM
  // ═════════════════════════════════════════════════════════════════
  if (view === "form") {
    const steps = ["Your Info", "Visa & Timeline", "Readiness"];

    return (
      <PageWrapper
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 28,
        }}
      >
        {/* Logo link back */}
        <div
          onClick={() => setView("landing")}
          style={{
            fontFamily: "Fira Sans, sans-serif",
            fontWeight: 800,
            fontSize: "1.3rem",
            color: C.navy,
            cursor: "pointer",
            marginBottom: 20,
          }}
        >
          KiwiOra
        </div>

        {/* Progress bar */}
        <div style={{ width: "100%", maxWidth: 380, padding: "0 24px" }}>
          <ProgressBar>
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div style={{ textAlign: "center" }}>
                  <ProgressDot $done={i < step} $active={i === step}>
                    {i < step ? "✓" : i + 1}
                  </ProgressDot>
                  <ProgressLabel $active={i === step}>{s}</ProgressLabel>
                </div>
                {i < steps.length - 1 && <ProgressLine $done={i < step} />}
              </React.Fragment>
            ))}
          </ProgressBar>
        </div>

        <FormSection style={{ marginTop: 0 }}>
          <FormWrap>
            <FormTitle>
              {step === 0
                ? "Tell us about yourself"
                : step === 1
                ? "Your Visa & Timeline"
                : "How ready are you?"}
            </FormTitle>
            <FormSubtitle>
              {step === 0
                ? "So we can reach you with a personalised plan."
                : step === 1
                ? "Help us match the right pathway for you."
                : "This helps us prioritize your consultation."}
            </FormSubtitle>

            <FormCard>
              {/* ──── Step 0: Contact ──── */}
              {step === 0 && (
                <>
                  <Row>
                    <FieldGroup>
                      <Label>First Name *</Label>
                      <Input
                        value={form.firstName}
                        onChange={(e) => set("firstName", e.target.value)}
                        placeholder="First Name"
                        $error={errors.firstName}
                      />
                      {errors.firstName && <Err>{errors.firstName}</Err>}
                    </FieldGroup>
                    <FieldGroup>
                      <Label>Last Name *</Label>
                      <Input
                        value={form.lastName}
                        onChange={(e) => set("lastName", e.target.value)}
                        placeholder="Last Name"
                        $error={errors.lastName}
                      />
                      {errors.lastName && <Err>{errors.lastName}</Err>}
                    </FieldGroup>
                  </Row>

                  <FieldGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@example.com"
                      $error={errors.email}
                    />
                    {errors.email && <Err>{errors.email}</Err>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label>Phone *</Label>
                    <PhoneInput
                      country={"in"}
                      enableSearch
                      placeholder="Enter phone number"
                      value={phone}
                      onChange={(value, country) => {
                        setPhone(value);
                        setCountryCode(country?.dialCode || "");
                        setErrors((e) => ({ ...e, phone: undefined }));
                      }}
                      inputStyle={{
                        width: "100%",
                        height: "38px",
                        fontSize: "0.9rem",
                        borderRadius: "9px",
                        border: `1.5px solid ${
                          errors.phone ? C.red : C.inputBorder
                        }`,
                        background: C.inputBg,
                        color: C.textDark,
                        fontFamily: "inherit",
                      }}
                      buttonStyle={{
                        border: `1.5px solid ${
                          errors.phone ? C.red : C.inputBorder
                        }`,
                        background: C.inputBg,
                        borderRadius: "9px 0 0 9px",
                      }}
                      dropdownStyle={{ zIndex: 1200 }}
                    />
                    {errors.phone && <Err>{errors.phone}</Err>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label>City (optional)</Label>
                    <Input
                      value={form.city}
                      onChange={(e) => set("city", e.target.value)}
                      placeholder="e.g. Hyderabad"
                    />
                  </FieldGroup>
                </>
              )}

              {/* ──── Step 1: Visa & Timeline ──── */}
              {step === 1 && (
                <>
                  <FieldGroup>
                    <Label>Current / Highest Education</Label>
                    <Input
                      value={form.education}
                      onChange={(e) => set("education", e.target.value)}
                      placeholder="e.g. B.Tech in CSE, 3rd year"
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <Label>Visa Type *</Label>
                    <OptionGrid cols={2}>
                      {VISA_TYPES.map((v) => (
                        <OptionBtn
                          key={v}
                          type="button"
                          $active={form.visaType === v}
                          onClick={() => set("visaType", v)}
                        >
                          {v}
                        </OptionBtn>
                      ))}
                    </OptionGrid>
                    {errors.visaType && <Err>{errors.visaType}</Err>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label>When do you plan to apply? *</Label>
                    <OptionGrid cols={2}>
                      {TIMELINES.map((t) => (
                        <OptionBtn
                          key={t}
                          type="button"
                          $active={form.timeline === t}
                          onClick={() => set("timeline", t)}
                        >
                          {t}
                        </OptionBtn>
                      ))}
                    </OptionGrid>
                    {errors.timeline && <Err>{errors.timeline}</Err>}
                  </FieldGroup>
                </>
              )}

              {/* ──── Step 2: Readiness ──── */}
              {step === 2 && (
                <>
                  <FieldGroup>
                    <Label>Estimated Budget *</Label>
                    <OptionGrid cols={2}>
                      {BUDGETS.map((b) => (
                        <OptionBtn
                          key={b}
                          type="button"
                          $active={form.budget === b}
                          onClick={() => set("budget", b)}
                        >
                          {b}
                        </OptionBtn>
                      ))}
                    </OptionGrid>
                    {errors.budget && <Err>{errors.budget}</Err>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label>IELTS / PTE / TOEFL? *</Label>
                    <OptionGrid cols={1}>
                      {TEST_OPTIONS.map((t) => (
                        <OptionBtn
                          key={t}
                          type="button"
                          $active={form.testScore === t}
                          onClick={() => set("testScore", t)}
                        >
                          {t}
                        </OptionBtn>
                      ))}
                    </OptionGrid>
                    {errors.testScore && <Err>{errors.testScore}</Err>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label>How will you fund your studies? *</Label>
                    <OptionGrid cols={2}>
                      {FUNDING.map((f) => (
                        <OptionBtn
                          key={f}
                          type="button"
                          $active={form.funding === f}
                          onClick={() => set("funding", f)}
                        >
                          {f}
                        </OptionBtn>
                      ))}
                    </OptionGrid>
                    {errors.funding && <Err>{errors.funding}</Err>}
                  </FieldGroup>

                  <FieldGroup>
                    <Label>Anything else? (optional)</Label>
                    <Input
                      as="textarea"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Tell us more about your plans..."
                      style={{ minHeight: 70, resize: "vertical" }}
                    />
                  </FieldGroup>
                </>
              )}

              {/* ──── Navigation ──── */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 18,
                }}
              >
                {step > 0 ? (
                  <NavBtn onClick={() => setStep((s) => s - 1)}>Back</NavBtn>
                ) : (
                  <NavBtn onClick={() => setView("landing")}>Cancel</NavBtn>
                )}

                {step < 2 ? (
                  <NextBtn onClick={handleNext}>Continue </NextBtn>
                ) : (
                  <SendBtn
                    style={{ width: "auto", padding: "11px 28px", marginTop: 0 }}
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Sending..." : "Get My Free Consultation"}
                  </SendBtn>
                )}
              </div>
            </FormCard>
          </FormWrap>

          <div
            style={{
              fontSize: "0.75rem",
              color: C.gray400,
              textAlign: "center",
              marginTop: 14,
            }}
          >
            🔒 Your information is secure and never shared with third parties.
          </div>
        </FormSection>
      </PageWrapper>
    );
  }

  // ═════════════════════════════════════════════════════════════════
  //  THANK YOU
  // ═════════════════════════════════════════════════════════════════
  if (view === "thankyou")
    return (
      <PageWrapper
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <ThankYouCard>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: 800,
              color: C.navy,
              margin: "0 0 8px",
            }}
          >
            You're on your way!
          </h2>

          <p
            style={{
              fontSize: "0.92rem",
              color: C.gray600,
              lineHeight: 1.6,
              marginBottom: 20,
            }}
          >
            Thank you, <strong>{lastLead?.firstName}</strong>! Our KIWIORA
            counselor will reach out within 24 hours with a personalised plan
            for your{" "}
            <strong>{lastLead?.visaType || "New Zealand"}</strong>{" "}
            application.
          </p>

          {lastLead && (
            <div
              style={{
                background:
                  lastLead.tier === "Hot"
                    ? "rgba(192, 57, 43, 0.08)"
                    : lastLead.tier === "Warm"
                    ? C.goldSoft
                    : C.lightGray,
                border: `1px solid ${C.goldBorder}`,
                borderRadius: 12,
                padding: "14px 18px",
                marginBottom: 22,
                textAlign: "left",
              }}
            >
              <div
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: C.gray400,
                  marginBottom: 6,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Your Readiness Profile
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <TierBadge tier={lastLead.tier}>{lastLead.tier}</TierBadge>
                <span
                  style={{
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: C.navy,
                  }}
                >
                  Score: {lastLead.score}/100
                </span>
              </div>
              <div
                style={{
                  fontSize: "0.78rem",
                  color: C.gray600,
                  lineHeight: 1.5,
                }}
              >
                {lastLead.reasons.join(" · ")}
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <CtaBtnDark
              onClick={() => setView("landing")}
              style={{ fontSize: "0.9rem", padding: "11px 24px" }}
            >
              Back to Home
            </CtaBtnDark>
          </div>
        </ThankYouCard>
      </PageWrapper>
    );

  // ═════════════════════════════════════════════════════════════════
  //  ADMIN LOGIN GATE
  // ═════════════════════════════════════════════════════════════════
  if (view === "login" || (view === "admin" && !adminAuthed))
    return (
      <PageWrapper
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            background: C.cardBg,
            borderRadius: 20,
            padding: "44px 32px",
            maxWidth: 380,
            width: "100%",
            textAlign: "center",
            border: `1px solid ${C.goldBorder}`,
            borderTop: `3px solid ${C.gold}`,
            boxShadow: C.goldShadow,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: C.inputBg,
              border: `2px solid ${C.gold}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              fontSize: 24,
            }}
          >
            🔒
          </div>
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: C.navy,
              margin: "0 0 6px",
            }}
          >
            Admin Access
          </h2>
          <p
            style={{
              fontSize: "0.85rem",
              color: C.gray400,
              marginBottom: 22,
            }}
          >
            Enter the dashboard password to continue.
          </p>

          <Input
            type="password"
            value={pwInput}
            onChange={(e) => {
              setPwInput(e.target.value);
              setPwError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()}
            placeholder="Password"
            $error={!!pwError}
            style={{ marginBottom: 8, textAlign: "center", fontSize: "1rem" }}
          />
          {pwError && <Err style={{ marginBottom: 8 }}>{pwError}</Err>}

          <SendBtn onClick={handleAdminLogin} style={{ marginTop: 8 }}>
            Log In
          </SendBtn>

          <div style={{ marginTop: 16 }}>
            <CtaBtnOutline
              onClick={() => setView("landing")}
              style={{ fontSize: "0.82rem" }}
            >
              ← Back to site
            </CtaBtnOutline>
          </div>
        </div>
      </PageWrapper>
    );

  // ═════════════════════════════════════════════════════════════════
  //  ADMIN DASHBOARD (password-protected)
  // ═════════════════════════════════════════════════════════════════
  if (view === "admin" && adminAuthed)
    return (
      <PageWrapper>
        <AdminNav>
          <div style={{ fontWeight: 700, fontSize: "1rem" }}>
             KiwiOra - Lead Dashboard
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <CtaBtnOutline
              style={{ color: C.white, borderColor: "rgba(255,255,255,0.2)" }}
              onClick={() => setView("landing")}
            >
              View Landing Page
            </CtaBtnOutline>
            <CtaBtnOutline
              style={{
                color: "#ff9b9b",
                borderColor: "rgba(255,155,155,0.3)",
              }}
              onClick={handleAdminLogout}
            >
              Log Out
            </CtaBtnOutline>
          </div>
        </AdminNav>

        <div
          style={{ padding: "22px 20px", maxWidth: 960, margin: "0 auto" }}
        >
          {/* Fetch error banner */}
          {fetchError && (
            <div
              style={{
                background: "rgba(192, 57, 43, 0.08)",
                border: `1px solid ${C.goldBorder}`,
                color: C.red,
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: "0.82rem",
                marginBottom: 16,
              }}
            >
              {fetchError}
            </div>
          )}

          {/* Stats */}
          <StatsRow>
            {[
              {
                l: "Total Leads",
                v: stats.total,
                c: C.navy,
                bg: C.cardBg,
              },
              { l: "Hot", v: stats.hot, c: C.red, bg: "rgba(192, 57, 43, 0.08)" },
              { l: "Warm", v: stats.warm, c: C.orange, bg: C.goldSoft },
              {
                l: "Cold",
                v: stats.cold,
                c: C.gray400,
                bg: C.lightGray,
              },
            ].map((s, i) => (
              <StatBox key={i} bg={s.bg}>
                <StatLabel2>{s.l}</StatLabel2>
                <StatValue color={s.c}>{s.v}</StatValue>
              </StatBox>
            ))}
          </StatsRow>

          {/* Filters */}
          <FilterRow>
            <div style={{ display: "flex", gap: 6 }}>
              {["All", "Hot", "Warm", "Cold"].map((f) => (
                <FilterBtn
                  key={f}
                  $active={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </FilterBtn>
              ))}
            </div>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, city..."
            />
            <RefreshBtn onClick={refreshLeads} disabled={loadingLeads}>
              {loadingLeads ? "Loading..." : "⟳ Refresh"}
            </RefreshBtn>
            <ExportBtn
              onClick={exportCSV}
              disabled={filtered.length === 0}
            >
              ↓ Export CSV
            </ExportBtn>
          </FilterRow>

          {/* Lead list */}
          {loadingLeads ? (
            <EmptyState>
              <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
              <div style={{ fontWeight: 600, color: C.navy, marginBottom: 4 }}>
                Loading leads…
              </div>
              <div style={{ fontSize: "0.85rem", color: C.gray400 }}>
                Pulling the latest from your Google Sheet.
              </div>
            </EmptyState>
          ) : filtered.length === 0 ? (
            <EmptyState>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              <div
                style={{
                  fontWeight: 600,
                  color: C.navy,
                  marginBottom: 4,
                }}
              >
                No leads yet
              </div>
              <div style={{ fontSize: "0.85rem", color: C.gray400 }}>
                Leads will appear here once someone fills out the form.
              </div>
            </EmptyState>
          ) : (
            filtered.map((lead) => (
              <LeadCard key={lead.id}>
                {/* Name & contact */}
                <div style={{ flex: "1 1 190px", minWidth: 170 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 3,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: C.navy,
                      }}
                    >
                      {lead.firstName} {lead.lastName}
                    </span>
                    <TierBadge tier={lead.tier}>
                      {lead.tier} ({lead.score})
                    </TierBadge>
                  </div>
                  <div
                    style={{ fontSize: "0.82rem", color: C.gray600 }}
                  >
                    {lead.email}
                  </div>
                  <div
                    style={{ fontSize: "0.78rem", color: C.gray400 }}
                  >
                    {lead.phone}
                    {lead.city ? ` · ${lead.city}` : ""}
                  </div>
                </div>

                {/* Details */}
                <div
                  style={{
                    flex: "1 1 200px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "3px 10px",
                    fontSize: "0.78rem",
                    color: C.gray600,
                  }}
                >
                  <span>📋 {lead.visaType}</span>
                  <span>⏱ {lead.timeline}</span>
                  <span>💰 {lead.budget}</span>
                  <span>📝 {lead.testScore}</span>
                  <span>🏦 {lead.funding}</span>
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  <select
                    value={lead.status}
                    onChange={(e) =>
                      updateStatus(lead.id, e.target.value)
                    }
                    style={{
                      padding: "5px 8px",
                      borderRadius: 8,
                      border: `1px solid ${C.gray200}`,
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      color: C.navy,
                      background: C.cardBg,
                      fontFamily: "inherit",
                    }}
                  >
                    {[
                      "New",
                      "Contacted",
                      "Qualified",
                      "Enrolled",
                      "Lost",
                    ].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => deleteLead(lead.id)}
                    style={{
                      background: "none",
                      border: "none",
                      color: C.gray400,
                      cursor: "pointer",
                      fontSize: 16,
                      padding: "3px 5px",
                    }}
                    title="Remove from this view"
                  >
                    ×
                  </button>

                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: C.gray400,
                      minWidth: 68,
                      textAlign: "right",
                    }}
                  >
                    {fmtDate(lead.createdAt)}
                  </div>
                </div>
              </LeadCard>
            ))
          )}
        </div>
      </PageWrapper>
    );

  return null;
}